import React, {useState, useEffect, useRef} from "react";
import {Container, Row} from "reactstrap";
import ApiService from "../util/ApiService";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import locale from "antd/es/date-picker/locale/pt_BR";
import dayjs from "dayjs";
import HospitalSearch from "../hospital/HospitalSearch";
import UsersSearch from "../dashboard/calender/UsersSearch";
import {DatePicker} from "antd";
import AuthService from "../login/AuthService";

const {RangePicker} = DatePicker;

document.title = "Relatório Ranking | Plantão Rápido";

const RANKING_TYPES = [
    {value: "repasses", label: "Repasses"},
    {value: "plantoes", label: "Quantidade de Plantões"},
    {value: "horas", label: "Horas Trabalhadas"},
];

const DutyRankingPage = () => {
    const [exportLoading, setExportLoading] = useState(false);
    const user = AuthService.getUserData();
    const refUserSearch = useRef(null);
    const hospitalSearchRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [rankingType, setRankingType] = useState("repasses");

    const today = dayjs();
    const endOfNextMonth = dayjs().add(1, 'month').endOf('month');
    const [filters, setFilters] = useState({
        dateRange: [today, endOfNextMonth],
        datestart: today.format("YYYY-MM-DD"),
        dateend: endOfNextMonth.format("YYYY-MM-DD"),
        hospital: "",
    });

    const handleSearchFiltersChange = (key, value) => {
        setFilters(prev => ({...prev, [key]: value}));
    };

    const onHospitalSelect = (hospital) => {
        handleSearchFiltersChange('hospital', hospital?.value || null);
    };

    const fetchRanking = async () => {
        setLoading(true);
        try {
            const response = await ApiService.send("post", `${process.env.REACT_APP_URL_API}scale/ranking`, {
                hospital: filters.hospital,
                datestart: filters.datestart,
                dateend: filters.dateend,
                user: filters.user_id,
                type: rankingType,
                ...(user.users_type_id === 1 && {logged_in_user: true}),
            }, true);

            const {data} = ApiService.handleApiResponse(response);
            setColumns(data.columns || []);
            setRows(data.rows || []);
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRanking();
    }, []);

    const handleClearFilters = () => {
        setFilters({
            dateRange: "",
            datestart: null,
            dateend: null,
            hospital: "",
        });
        if (hospitalSearchRef.current) hospitalSearchRef.current.clearHospital();
        if (refUserSearch.current) refUserSearch.current.clearUser();
    };

    const handleExport = async () => {
        try {
            setExportLoading(true);
            const response = await ApiService.send(
                "post",
                `${process.env.REACT_APP_URL_API}scale/ranking/export`,
                {
                    hospital: filters.hospital,
                    datestart: filters.datestart,
                    dateend: filters.dateend,
                    user: filters.user_id,
                    type: rankingType,
                },
                true,
                'blob'
            );

            const blob = new Blob([response], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `ranking_${rankingType}_${new Date().toISOString().split('T')[0]}.xlsx`;
            link.click();
            URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Relatório Ranking" pageTitle="Plantões"/>
                    <Row className="g-4 mb-4">
                        <div className="row mt-1">
                            <div className="col-md-3">
                                <div className="form-group">
                                    <label>Tipo</label>
                                    <select
                                        className="form-select"
                                        value={rankingType}
                                        onChange={(e) => setRankingType(e.target.value)}
                                    >
                                        {RANKING_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-group">
                                    <label>Data</label>
                                    <div className="form-control">
                                        <RangePicker
                                            style={{border: "none", boxShadow: "none"}}
                                            locale={locale}
                                            format="DD/MM/YYYY"
                                            className="range-picker-custom"
                                            value={filters.dateRange.length > 0 ? filters.dateRange : null}
                                            onChange={(dates) => {
                                                if (dates) {
                                                    handleSearchFiltersChange("dateRange", dates);
                                                    handleSearchFiltersChange("datestart", dayjs(dates[0]).format("YYYY-MM-DD"));
                                                    handleSearchFiltersChange("dateend", dayjs(dates[1]).format("YYYY-MM-DD"));
                                                } else {
                                                    handleSearchFiltersChange("dateRange", []);
                                                    handleSearchFiltersChange("datestart", null);
                                                    handleSearchFiltersChange("dateend", null);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <HospitalSearch
                                    ref={hospitalSearchRef}
                                    buttonAdd={false}
                                    cache={false}
                                    validateManager={true}
                                    onHospitalSelect={onHospitalSelect}
                                />
                            </div>
                            <div className="col-md-3 form-group">
                                <UsersSearch
                                    ref={refUserSearch}
                                    onUserSelect={(u) =>
                                        setFilters((prev) => ({...prev, user_id: u?.value || ""}))
                                    }
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-12 d-flex justify-content-end">
                                <button type="button" className="btn mr-1 me-2" onClick={handleClearFilters}>
                                    <i className="fa fa-eraser"></i> Limpar Filtros
                                </button>
                                <button type="button" className="btn btn-success" onClick={fetchRanking} disabled={loading}>
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Buscando...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-search me-2"></i> Buscar
                                        </>
                                    )}
                                </button>
                                <button
                                    className="btn btn-outline-primary ms-2"
                                    onClick={handleExport}
                                    disabled={exportLoading || rows.length === 0}
                                >
                                    {exportLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status"/>
                                            Exportando...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-download me-1"></i> Exportar
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="container-fluid p-4">
                            {loading ? (
                                <Skeleton height={300} count={1}/>
                            ) : (
                                <div className="card p-3">
                                    <div className="table-responsive">
                                        <table className="table table-hover">
                                            <thead>
                                            <tr>
                                                {columns.map((col) => (
                                                    <th key={col.key}>{col.label}</th>
                                                ))}
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {rows.length > 0 ? rows.map((row, index) => (
                                                <tr key={index}>
                                                    {columns.map((col) => (
                                                        <td key={col.key}>
                                                            {col.key === 'position' ? (
                                                                <span className={`badge ${row[col.key] <= 3 ? 'bg-warning' : 'bg-secondary'}`}>
                                                                    {row[col.key]}º
                                                                </span>
                                                            ) : (
                                                                row[col.key]
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan={columns.length || 1} className="text-center py-4 text-muted">
                                                        Nenhum resultado encontrado no período selecionado.
                                                    </td>
                                                </tr>
                                            )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default DutyRankingPage;
